import { Skeleton } from '@chakra-ui/react';
import React, { createContext, useContext, useMemo } from 'react';

import { finleyAssert } from '@finley-technologies/common';

import { useGetOrganizationLoanProductsByCreditFacilityQuery } from '~/packages/organization/requests/organization.gql.generated';

type SlugsToNames = {
  creditFacilities: Map<string, string>;
  loanProducts: Map<string, string>;
};

const SlugToNameContext = createContext<SlugsToNames | null>(null);

export const SlugToNameProvider = ({
  children,
  organizationId,
  loadingComponent,
}: {
  children: React.ReactNode;
  organizationId: string;
  loadingComponent?: React.ReactNode;
}) => {
  const getOrgFacilityLoanProductQuery = useGetOrganizationLoanProductsByCreditFacilityQuery(
    { variables: { organizationId } },
  );
  if (getOrgFacilityLoanProductQuery.error) {
    throw getOrgFacilityLoanProductQuery.error;
  }

  const slugToNames: SlugsToNames | null = useMemo(() => {
    if (!getOrgFacilityLoanProductQuery.data) {
      return null;
    }

    const creditFacilities = new Map<string, string>();
    const loanProducts = new Map<string, string>();

    getOrgFacilityLoanProductQuery.data.result.creditFacilities.forEach(
      (cf) => {
        creditFacilities.set(cf.slug, cf.name);
        cf.loanProducts.forEach((lp) => {
          loanProducts.set(lp.slug, lp.name);
        });
      },
    );

    return { creditFacilities, loanProducts };
  }, [getOrgFacilityLoanProductQuery.data]);

  if (!slugToNames) {
    return (
      loadingComponent ?? (
        <Skeleton height="600px" data-testid="slugToNameProvider-loading" />
      )
    );
  }

  return (
    <SlugToNameContext.Provider value={slugToNames}>
      {children}
    </SlugToNameContext.Provider>
  );
};

export const useSlugToName = () => {
  const context = useContext(SlugToNameContext);
  finleyAssert(
    context,
    'useSlugToName must be used within a SlugToNameProvider',
  );

  return useMemo(() => {
    return {
      cfSlugToName: (slug: string): string => {
        const name = context.creditFacilities.get(slug);
        finleyAssert(name, `Credit facility with [slug:${slug}] not found`);
        return name;
      },
      lpSlugToName: (slug: string): string => {
        const name = context.loanProducts.get(slug);
        finleyAssert(name, `Loan product with [slug:${slug}] not found`);
        return name;
      },
    };
  }, [context.creditFacilities, context.loanProducts]);
};
